import * as ToastPrimitive from "@radix-ui/react-toast";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { cn } from "../lib/cn";
import { TONE_CLASSES, type Tone } from "../lib/styles";
import { Dot } from "./Badge";
import { Button } from "./Button";
import { Icon } from "./Icon";

export interface ToastSpec {
  id: string;
  tone: Tone;
  /** (H) doc 41 §6: lead with what happened, and name the actor. */
  title: string;
  description?: string;
  /** Data attributes for the toast's root, e.g. `data-problem-kind` (D239). */
  attributes?: Record<`data-${string}`, string>;
  /** [D239] Stays until closed. A refusal must not vanish while it is being read. */
  persistent?: boolean;
  /** Present for anything a user might need to undo or inspect. */
  action?: { label: string; onSelect: () => void };
}

interface ToastApi {
  /** Returns the toast's id, for `dismiss`. */
  toast: (spec: Omit<ToastSpec, "id">) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastApi | null>(null);
const MAX_PERSISTENT = 3;

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside <ToastProvider>.");
  return ctx;
}

/**
 * A toast is a flat white card with a tone dot — not a coloured surface
 * (doc 40 §1: colour is an accent, not a surface). The tone appears as the
 * dot and in the title colour, the same dot-plus-label pattern as every other
 * status in the system, so a user learns one vocabulary.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastSpec[]>([]);

  // Read synchronously by `toast` to find a duplicate; state alone updates too late for that.
  const itemsRef = useRef<ToastSpec[]>([]);
  itemsRef.current = items;

  const toast = useCallback((spec: Omit<ToastSpec, "id">) => {
    const id = `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // [D239] Failures stay until closed, so cap them: the oldest of more than three goes.
    const kept = itemsRef.current.filter((t) => t.persistent);
    const drop =
      spec.persistent && kept.length >= MAX_PERSISTENT
        ? kept[0]?.id
        : undefined;
    const next = [
      ...itemsRef.current.filter((t) => t.id !== drop),
      { ...spec, id },
    ];
    itemsRef.current = next;
    setItems(next);
    return id;
  }, []);

  const dismiss = useCallback((id: string) => {
    const next = itemsRef.current.filter((t) => t.id !== id);
    itemsRef.current = next;
    setItems(next);
  }, []);

  const api = useMemo<ToastApi>(() => ({ toast, dismiss }), [toast, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      <ToastPrimitive.Provider swipeDirection="right" duration={6000}>
        {children}
        {items.map((t) => (
          <ToastPrimitive.Root
            key={t.id}
            {...(t.persistent ? { duration: Number.POSITIVE_INFINITY } : {})}
            data-tone={t.tone}
            // Radix announces a toast in its own live region; a failure interrupts.
            type={t.tone === "danger" ? "foreground" : "background"}
            {...t.attributes}
            onOpenChange={(open) => {
              if (!open) dismiss(t.id);
            }}
            className={cn(
              "flex items-start gap-2.5 rounded-lg border p-3 shadow-menu",
              // [D239] A failure is the one toast that is a red surface: it has to be seen.
              t.tone === "danger"
                ? "border-danger border-l-4 bg-danger-weak"
                : "border-default bg-default",
              "data-[state=open]:animate-fade-in motion-reduce:animate-none",
            )}
          >
            <span className="mt-1.5">
              <Dot tone={t.tone} />
            </span>
            <div className="min-w-0 flex-1">
              <ToastPrimitive.Title
                className={cn("text-sm font-medium", TONE_CLASSES[t.tone].text)}
              >
                {t.title}
              </ToastPrimitive.Title>
              {t.description ? (
                <ToastPrimitive.Description className="mt-1 whitespace-pre-line text-xs text-secondary">
                  {t.description}
                </ToastPrimitive.Description>
              ) : null}
            </div>
            {t.action ? (
              <ToastPrimitive.Action asChild altText={t.action.label}>
                <Button
                  variant="tertiary"
                  size="sm"
                  onClick={t.action.onSelect}
                >
                  {t.action.label}
                </Button>
              </ToastPrimitive.Action>
            ) : null}
            <ToastPrimitive.Close asChild>
              <Button variant="ghost" size="sm" iconOnly aria-label="Dismiss">
                <Icon name="close" size={4} />
              </Button>
            </ToastPrimitive.Close>
          </ToastPrimitive.Root>
        ))}
        <ToastPrimitive.Viewport className="fixed bottom-4 right-4 z-toast flex w-full max-w-sm flex-col gap-2 outline-none" />
      </ToastPrimitive.Provider>
    </ToastContext.Provider>
  );
}
